import { useId, useRef, type ReactNode } from 'react';
import { COMMON } from '@locales/id/messages';
import { useBodyClass } from '@lib/hooks';
import { IconButton } from './IconButton';
import { useExitGhost } from './useExitGhost';
import { useModalFocus } from './useModalFocus';

/**
 * Modal dialog (only the topmost of several answers Esc: a confirm over a form closes alone). `dismissible={false}`
 * (busy, or a one-time secret) ignores Esc, backdrop and the X button. Once something was typed or chosen inside, a tap on the backdrop no longer closes it (a mis-tap on a phone
 * must not throw away a half-filled form); Esc, X and Batal still do.
 */
export function Dialog({ title, onClose, children, footer, wide, dismissible = true }: { title: string; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean; dismissible?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const back = useRef<HTMLDivElement>(null);
  // closed by anyone (X, Esc, its caller after a save): it fades out instead of vanishing
  useExitGhost(back, 'dialog');
  const close = useRef(onClose);
  close.current = () => {
    if (canDismiss.current) onClose();
  };
  const canDismiss = useRef(dismissible);
  canDismiss.current = dismissible;
  const edited = useRef(false);
  const titleId = useId();
  useBodyClass('no-scroll');
  useModalFocus(ref, () => close.current());
  return (
    <div ref={back} className="dialog-back" onMouseDown={(e) => e.target === e.currentTarget && !edited.current && close.current()}>
      <div className={`dialog ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId} ref={ref}>
        <header>
          <h2 id={titleId}>{title}</h2>
          {dismissible && <IconButton icon="close" label={COMMON.close} onClick={onClose} />}
        </header>
        <div className="dialog-body" onInput={() => (edited.current = true)}>{children}</div>
        {footer && <footer>{footer}</footer>}
      </div>
    </div>
  );
}
